import { useCallback } from 'react';

import { soundEngine } from '../services/audio';
import { selectActiveTradeVisual, useTradingStore } from '../store';
import type { CloseReason, ClosedTrade, PositionType } from '../types';
import { computePnl } from '../utils/pnl';

export interface CloseVisualsParams {
    entry: number;
    exitPrice: number;
    posType: PositionType;
    posAmount: number;
    /** Market the trade was on ("BTC"); stamped onto the closed-trade row so
     *  history never relabels itself with whatever pair is selected later. */
    coin?: string;
    /** Position leverage; PnL is return-on-collateral. Defaults to 1x. */
    leverage?: number;
    closeReason?: CloseReason;
    /** When provided, duplicate-prevention is applied via this hash. */
    tradeHash?: string;
    /** Measured request→close latency (ms); rides the POSITION CLOSED card on
     *  the latency-debug hosts only (absent on WS/reconciliation closes). */
    closeMs?: number;
}

interface UseTradeCloseVisualsResult {
    fireCloseVisuals: (params: CloseVisualsParams) => ClosedTrade | null;
}

// Module-scoped dedup tracking so all callers share the same guard.
let lastFiredTradeHash: string | null = null;

/**
 * Provides `fireCloseVisuals` — plays sounds, triggers chart pulse, shows
 * the overlay trade and toast. Reads triggerPulse / setOverlayTrade from the
 * trading store so callers don't need to pass them.
 */
export function useTradeCloseVisuals(): UseTradeCloseVisualsResult {
    const fireCloseVisuals = useCallback(
        (params: CloseVisualsParams): ClosedTrade | null => {
            const {
                entry,
                exitPrice,
                posType,
                posAmount,
                leverage = 1,
                closeReason,
                tradeHash,
                coin,
                closeMs,
            } = params;

            if (tradeHash) {
                if (lastFiredTradeHash === tradeHash) return null;
                lastFiredTradeHash = tradeHash;
            }

            const { pnlPercent, pnlValue } = computePnl(
                entry,
                exitPrice,
                posType,
                posAmount,
                leverage,
                closeReason,
            );

            if (pnlValue >= 0) {
                soundEngine.playProfit();
            } else {
                soundEngine.playLoss();
            }

            const store = useTradingStore.getState();
            store.triggerPulse(pnlValue >= 0 ? 'win' : 'loss');

            // Open timestamp for the "held for" readout (share card).
            const openedAt = selectActiveTradeVisual(
                useTradingStore.getState(),
            )?.timestamp;

            const closedTrade: ClosedTrade = {
                type: posType,
                entryPrice: entry,
                exitPrice,
                amount: posAmount,
                leverage,
                pnlValue,
                pnlPercent,
                timestamp: Date.now(),
                ...(closeReason ? { closeReason } : {}),
                ...(openedAt !== undefined ? { openedAt } : {}),
                ...(coin ? { coin: coin.toUpperCase() } : {}),
                ...(closeMs !== undefined ? { closeMs } : {}),
            };

            // The top-center POSITION CLOSED card (SpeedResultFx) renders
            // off this overlay trade.
            store.setOverlayTrade(closedTrade);

            return closedTrade;
        },
        [],
    );

    return { fireCloseVisuals };
}
